// State for a clinical exam station (OSCE practice) opened on top of another
// page: from a set's own page, or offered in the middle of a review. The
// station itself is mounted once, globally, and shows whatever this store
// holds, so the page underneath keeps its state while the learner is away.
//
// This file is imported by code that runs at app startup (the review's
// checkpoint logic, the global modals). Keep it small, and never import the
// examination catalog from it.

import { create } from "zustand";

// "direct_link" is the station's own address, which nothing links to.
// "first_review": in place of the voice check at the end of a health student's first review (the
// first-experience test T2c, firstExperienceGelRules.ts).
export type ClinicalExamStationEntryPoint = "direct_link" | "deck" | "review" | "first_review";

// The set a station was opened from. Steps the learner misses become
// questions in it.
export interface ClinicalExamStationDeck {
  id: string;
  name: string;
}

export interface OpenClinicalExamStation {
  clinicalExamId: string;
  deck: ClinicalExamStationDeck | null;
  entryPoint: ClinicalExamStationEntryPoint;
  // Changes on every open, so two openings of the same station never share
  // a voice call, a clock or a checklist.
  openingNumber: number;
}

// What a review knows about one of its sets: its name and the examinations
// chosen when it was generated. An empty list means "looked, and none".
export interface ClinicalExamStationsOfDeck {
  deckName: string;
  clinicalExamIds: string[];
}

// The station a review may offer for one of its sets right now. Worked out
// ahead of the checkpoint by the review page, so the checkpoint itself only
// has to look it up.
export interface ClinicalExamStationDueInReview {
  clinicalExamId: string;
  deckName: string;
  // "beat_last_run" when the learner has a score under 100% to beat.
  offerKind: "door" | "beat_last_run";
  lastScorePercent: number | null;
}

interface ClinicalExamStationStoreState {
  openStation: OpenClinicalExamStation | null;
  // How many stations have been opened since the app loaded.
  openingCount: number;
  openClinicalExamStation: (station: Omit<OpenClinicalExamStation, "openingNumber">) => void;
  closeClinicalExamStation: () => void;

  // A review offers a station at most once per session (owner's rule).
  hasOfferedStationInThisReviewSession: boolean;
  startNewReviewSession: () => void;
  markStationOfferedInThisReviewSession: () => void;

  clinicalExamStationsByDeckId: Record<string, ClinicalExamStationsOfDeck>;
  setClinicalExamStationsOfDeck: (deckId: string, stationsOfDeck: ClinicalExamStationsOfDeck) => void;

  stationsDueInReviewByDeckId: Record<string, ClinicalExamStationDueInReview>;
  setStationsDueInReviewByDeckId: (stationsDueByDeckId: Record<string, ClinicalExamStationDueInReview>) => void;
}

export const useClinicalExamStationStore = create<ClinicalExamStationStoreState>((set) => ({
  openStation: null,
  openingCount: 0,
  openClinicalExamStation: (station) =>
    set((state) => ({
      openingCount: state.openingCount + 1,
      openStation: { ...station, openingNumber: state.openingCount + 1 },
    })),
  closeClinicalExamStation: () => set({ openStation: null }),

  hasOfferedStationInThisReviewSession: false,
  startNewReviewSession: () => set({ hasOfferedStationInThisReviewSession: false }),
  markStationOfferedInThisReviewSession: () => set({ hasOfferedStationInThisReviewSession: true }),

  clinicalExamStationsByDeckId: {},
  setClinicalExamStationsOfDeck: (deckId, stationsOfDeck) =>
    set((state) => ({
      clinicalExamStationsByDeckId: { ...state.clinicalExamStationsByDeckId, [deckId]: stationsOfDeck },
    })),

  stationsDueInReviewByDeckId: {},
  setStationsDueInReviewByDeckId: (stationsDueByDeckId) => set({ stationsDueInReviewByDeckId: stationsDueByDeckId }),
}));

// --- "not now" -------------------------------------------------------------
// A learner who turns a station down in a review is not asked again that day.
// Kept in localStorage, per user, so it holds across reloads; a browser that
// refuses storage just asks again, which is harmless.

const DECLINED_STATIONS_STORAGE_KEY = "jungle_clinical_exam_station_offers_declined";

type DeclinedStationOffers = Record<string, Record<string, number>>;

function readDeclinedStationOffers(): DeclinedStationOffers {
  try {
    const storedValue = window.localStorage.getItem(DECLINED_STATIONS_STORAGE_KEY);
    const parsedValue = storedValue ? JSON.parse(storedValue) : {};
    return parsedValue && typeof parsedValue === "object" ? (parsedValue as DeclinedStationOffers) : {};
  } catch {
    return {};
  }
}

export function getDeclinedClinicalExamStationOffers(userId: string): Record<string, number> {
  const declinedOffersForUser = readDeclinedStationOffers()[userId];
  return declinedOffersForUser && typeof declinedOffersForUser === "object" ? declinedOffersForUser : {};
}

export function rememberClinicalExamStationOfferDeclined(
  userId: string,
  clinicalExamId: string,
  nowInMilliseconds: number = Date.now()
): void {
  try {
    const declinedOffers = readDeclinedStationOffers();
    declinedOffers[userId] = { ...declinedOffers[userId], [clinicalExamId]: nowInMilliseconds };
    window.localStorage.setItem(DECLINED_STATIONS_STORAGE_KEY, JSON.stringify(declinedOffers));
  } catch {
    // Storage is unavailable; the offer simply comes back sooner.
  }
}
